// apps/cli/src/lib/prereq-deps.ts
//
// The real wiring of PrereqDeps. Lives in lib/ rather than in the command so
// every entry point (`m8t prereqs`, `bootstrap preflight`, `bootstrap status`)
// imports the SAME seam — and so a command test can mock one module instead of
// reaching live `az`.

import { DefaultAzureCredential } from "@azure/identity";
import { getAzAccount, getCallerObjectId } from "./auth.js";
import { checkAppRegCapability, checkSubScopeAdmin } from "./bootstrap-preflight.js";
import { readFoundryConfig } from "./foundry-config.js";
import { discoverGateway } from "./discovery.js";
import {
  fixFoundryAccess,
  fixKeyVaultAccess,
  probeFoundryAccess,
  probeKeyVaultAccess,
  probeModelQuota,
  probeProviders,
  probeRedirectUri,
  probeSoftDeletedAccounts,
  registerProviders,
  resolveFoundryAccountId,
  resolvePlatformKeyVault,
  resolvePrincipalObjectId,
} from "./prereq-probes.js";
import type { PrereqDeps } from "./prereq-run.js";

/** The reasoning model the installed workers run on — the one whose quota the install needs. */
export const INSTALL_REASONING_MODEL = "gpt-5.4";

export function buildPrereqDeps(opts: { resourceGroup?: string; subscription?: string } = {}): PrereqDeps {
  let credentialSingleton: DefaultAzureCredential | null = null;
  return {
    getAzAccount: () => getAzAccount().catch(() => null),
    getCallerObjectId: () => getCallerObjectId().catch(() => null),
    checkSubScopeAdmin,
    checkAppRegCapability,
    probeProviders,
    registerProviders,
    probeModelQuota,
    probeSoftDeletedAccounts,
    discoverPlatform: async () => {
      const cfg = await readFoundryConfig();
      // Only "there is no deployment here" means the install phase. Every other
      // discovery failure — several deployments in the subscription, an ARM read
      // that failed, a named resource group that does not exist — means a platform
      // may well exist and we could not resolve WHICH. Swallowing those into
      // `null` reported the install phase to someone with a live platform and
      // told them nothing was blocking them, about an install they were not doing.
      const d = await discoverGateway({
        interactive: false,
        ...(opts.resourceGroup ? { resourceGroup: opts.resourceGroup } : {}),
        ...(opts.subscription ? { subscriptionId: opts.subscription } : {}),
      });
      return {
        gatewayUrl: d.gatewayUrl,
        gatewayClientId: d.gatewayClientId,
        subscriptionId: d.subscriptionId,
        resourceGroup: d.containerAppResourceId.split("/resourceGroups/")[1]?.split("/")[0] ?? "",
        // The DEPLOYMENT is the authority, local config only a fallback. A
        // teammate joining a platform someone else installed has no
        // ~/.m8t/config.yaml at all — reading it first left the single most
        // important check reporting "could not determine" for exactly the person
        // this feature exists to serve.
        projectEndpoint: d.projectEndpoint ?? cfg?.projectEndpoint ?? null,
      };
    },
    isNoPlatformError: (e: unknown) => (e as { code?: string }).code === "GATEWAY_DISCOVERY_ZERO",
    resolveFoundryAccountId,
    resolvePlatformKeyVault,
    resolvePrincipalObjectId,
    probeFoundryAccess,
    probeKeyVaultAccess,
    probeRedirectUri,
    fixFoundryAccess,
    fixKeyVaultAccess,
    credential: () => (credentialSingleton ??= new DefaultAzureCredential()),
  };
}
